import { api } from 'domains/api';
import { BASE_API_ROUTES, getBaseAPIRoute } from 'domains/api/base/routes';
import { BaseClient, FullClient, getClientInformation } from 'domains/client';
import { ClientDataError, logClientDataError } from 'domains/contact/errors';
import { Contact } from './types';

export const getAssociatedClients = (): Promise<BaseClient[]> => {
  return api.base.get<Array<BaseClient>>(getBaseAPIRoute(BASE_API_ROUTES.contacts.clients));
};

export const getAssociatedClientsAndData = async (
  cognitoUsername: string,
): Promise<{
  clients: FullClient[];
  contactId: string;
}> => {
  // Get the associated client IDs
  const clients = await getAssociatedClients().catch(err => {
    throw new ClientDataError(`Bad user data, failed to fetch associated clients`, {
      cognitoUsername,
      originalErrorMessage: err,
    });
  });

  if (!clients.length) {
    throw new ClientDataError(`Bad user data, no associated clients (empty list)`, {
      cognitoUsername,
    });
  }

  const contactId = clients[0].contactID;

  const fullClientsData: FullClient[] = [];

  // Get the detailed data for each client
  (await Promise.allSettled(clients.map(client => getClientInformation(contactId, client.clientID)))).forEach(
    (result, index) => {
      if (result.status === 'fulfilled') {
        fullClientsData.push({
          ...result.value,
          relationshipToClient: clients[index].relationshipToClient,
        });
      } else {
        logClientDataError(
          new ClientDataError('Failed to fetch client data', {
            cognitoUsername,
            clientId: clients[index].clientID,
            contactId,
            originalErrorMessage: result.reason,
          }),
        );
      }
    },
  );

  return {
    clients: fullClientsData,
    contactId,
  };
};

export const getContact = (contactId: string): Promise<Contact> => {
  return api.base.get<Contact>(getBaseAPIRoute(BASE_API_ROUTES.contacts[':contactId'].get, { contactId }));
};
